import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useSubRoutes, useExactRedirect, usePermissionTabs } from 'hooks';
import { globalPropTypes, isApplyingMa, FEATURE_MA_YC_APPLY } from 'helpers';

import { Margin } from 'styled-components-spacing';
import { Header, Tabs } from 'components';
import { loadMaPeopleBarCreator } from 'store/actionsCreators';
import { useTranslation } from 'react-i18next';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ma.people',
    url: '/ma/people',
  },
];

const initialTabs = [
  {
    title: 'navigation.tabs.ma.people.memberAssociations',
    path: '/ma/people/users',
    key: 'users',
  },
  {
    title: 'navigation.tabs.ma.people.organisations',
    path: '/ma/people/organisations',
    key: 'organisations',
  },
  {
    title: 'navigation.tabs.ma.people.people',
    path: '/ma/people/list',
    key: 'list',
  },
  {
    title: 'navigation.tabs.ma.people.members',
    path: '/ma/people/members',
    key: 'members',
  },
  {
    title: 'navigation.tabs.ma.people.applied',
    path: '/ma/people/applied',
    key: 'applied',
  },
];

const People = ({ routes, loadBar, bar, userPermissions, applying }) => {
  const { t } = useTranslation();
  useEffect(() => {
    loadBar();
  }, []);

  // "Applying" MAs expose general contacts only (no board members / officer
  // positions), so the "MA users" role-assignment tab and add action are hidden.
  // "Applied for Membership" triages join requests, which are held back until
  // the client releases them.
  const hiddenTabs = [...(applying ? ['users'] : []), ...(FEATURE_MA_YC_APPLY ? [] : ['applied'])];
  const visibleTabs = initialTabs.filter((tab) => !hiddenTabs.includes(tab.key));

  const [renderSubRoutes] = useSubRoutes(routes);
  const [tabs, firstTabPath] = usePermissionTabs(visibleTabs, routes, userPermissions, bar);
  const [renderExactRedirect] = useExactRedirect('/ma/people', firstTabPath);

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={t('page.ma.people.title')}
        renderActions={({ Button: ButtonRender }) =>
          applying ? null : (
            <>
              <ButtonRender secondary as={Link} to="/ma/people/add">
                {t('page.ma.people.addMaUserButton')}
              </ButtonRender>
              {/* Granting an Honorary Fellowship: a different set of people, and a smaller set of
                  positions, so it gets its own screen rather than a mode on the one above. */}
              <Margin top={{ xs: 12 }}>
                <ButtonRender secondary as={Link} to="/ma/people/honorary/add">
                  {t('page.ma.people.addHonoraryButton')}
                </ButtonRender>
              </Margin>
            </>
          )
        }
      />
      <Tabs items={tabs} />
      {renderExactRedirect()}
      {renderSubRoutes()}
    </>
  );
};

People.propTypes = {
  routes: PropTypes.arrayOf(globalPropTypes.RoutePropTypes),
  loadBar: PropTypes.func.isRequired,
  bar: PropTypes.shape({
    users: PropTypes.number,
    organisations: PropTypes.number,
    list: PropTypes.number,
    members: PropTypes.number,
    youngcrew: PropTypes.number,
    applied: PropTypes.number,
  }).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  applying: PropTypes.bool,
};

People.defaultProps = {
  routes: [],
  applying: false,
};

const mapStateToProps = ({ ma, user }) => ({
  bar: ma.people.bar,
  userPermissions: user.userPermissions,
  applying: isApplyingMa(user.user.memberAssociation),
});

const mapDispatchToProps = (dispatch) => ({
  loadBar: () => dispatch(loadMaPeopleBarCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(People);
